import { useMemo } from 'react'
import { useParams } from 'react-router'

import { USE_CURRENCY_SYMBOL_UNIT } from '../../../app/constants'
import { Stats } from '../../../common/components'
import {
  useCostUtils,
  useDashboard,
  useQueryFilters,
  useTimeStatsQuery,
} from '../../../common/hooks'
import type { Tag } from '../../../common/types/common'
import { formatBuszAppData } from '../../../common/utils/dataFormatting'
import { decompressAndDecodeJSON } from '../../../common/utils/jsonCompression'
import {
  formatNumber,
  fractionToPercentage,
  getBestTimeFormatForSeconds,
} from '../../../common/utils/numberFormatting'
import { getAppNameFromContentTags } from '../utils'

const TimeStats = () => {
  const { tags_of_interest } = useQueryFilters()
  const { encodedAppTags } = useParams()
  const { company } = useDashboard()
  const { formatCost } = useCostUtils()

  const { tagFilters, appName } = useMemo(() => {
    const tagFilters = decompressAndDecodeJSON<Tag[]>(encodedAppTags as string)
    const appName = getAppNameFromContentTags(tagFilters)
    return { tagFilters, appName }
  }, [encodedAppTags])

  const variables = useMemo(() => ({ tag_filters: tagFilters }), [tagFilters])

  const { dataList, dailyComputerCountSum, isLoadingWindowUsage, isLoadingComputerCount } =
    useTimeStatsQuery('window', variables)

  const timeH = dataList?.reduce((sum: number, val) => sum + val.time_h, 0) ?? 0
  const [teams, annualCost, visits] = dataList ? formatBuszAppData(dataList) : []

  const favoriteUrl = '/business-apps/window-usage'
  const favoriteQueryOptions = {
    tags_of_interest: tags_of_interest.application,
    tag_filters: tagFilters,
    group_by_date: true,
  }
  const dailyHours = dailyComputerCountSum ? timeH / dailyComputerCountSum : 0

  const statData = [
    [
      {
        label: 'Annualized cost',
        value: formatCost(annualCost),
        params: {
          url: favoriteUrl,
          dataKey: 'annual_cost',
          queryOptions: favoriteQueryOptions,
          label: `${appName} annualized cost`,
          unit: USE_CURRENCY_SYMBOL_UNIT,
        },
      },
      {
        label: 'Total hours',
        value: formatNumber(timeH, 1),
        params: {
          url: favoriteUrl,
          dataKey: 'time_h',
          queryOptions: favoriteQueryOptions,
          label: `Hours in ${appName}`,
          unit: ' hours',
        },
      },
      {
        value: formatNumber(dailyHours, 10),
        label: 'Daily hours per computer',
        isLoading: isLoadingComputerCount,
      },
    ],
    [
      {
        label: 'Total visits',
        value: formatNumber(visits, 1) ?? '',
        params: {
          url: favoriteUrl,
          dataKey: 'visits',
          queryOptions: favoriteQueryOptions,
          label: `Visits in ${appName}`,
          unit: ' visits',
        },
      },
      {
        label: 'Avg. visit time',
        value: getBestTimeFormatForSeconds((timeH * 3600) / visits) ?? '',
      },
      {
        label: 'Teams',
        value: teams ?? '',
        description: `${fractionToPercentage(teams / company.teamCount)} of total`,
        params: {
          url: favoriteUrl,
          dataKey: 'team_count',
          queryOptions: favoriteQueryOptions,
          label: `Teams using ${appName}`,
          unit: ' teams',
        },
      },
    ],
  ]

  return (
    <div className='flex flex-col gap-2'>
      <Stats data={statData[0]} isLoading={isLoadingWindowUsage} />
      <Stats data={statData[1]} isLoading={isLoadingWindowUsage} />
    </div>
  )
}

export default TimeStats

